import { Button, Input } from '../components/admin/ui';
import { useEffect, useState } from 'react';
import {
  Plus,
  Trash2,
  X,
  Loader2,
  Webhook,
  CheckCircle2,
  XCircle,
  ToggleLeft,
  ToggleRight,
  Copy,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { adminApi, WebhookSubscription } from '../lib/admin-api';

const availableEvents = [
  'conversation.created',
  'conversation.updated',
  'conversation.resolved',
  'conversation.assigned',
  'message.sent',
  'message.ai_response',
  'handoff.requested',
  'handoff.completed',
  'sla.warning',
  'sla.breached',
  'csat.submitted',
];

export function WebhooksPage() {
  const { profile } = useAuth();
  const [webhooks, setWebhooks] = useState<WebhookSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState<Set<string>>(new Set());

  // Form state
  const [formUrl, setFormUrl] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formEvents, setFormEvents] = useState<string[]>([]);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    fetchWebhooks();
  }, [profile?.organization_id]);

  async function fetchWebhooks() {
    if (!profile?.organization_id) return;
    setLoading(true);
    const data = await adminApi.getWebhooks(profile.organization_id);
    setWebhooks(data);
    setLoading(false);
  }

  const resetForm = () => {
    setFormUrl('');
    setFormDescription('');
    setFormEvents([]);
    setFormError('');
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!formUrl.trim()) {
      setFormError('URL is required');
      return;
    }
    if (formEvents.length === 0) {
      setFormError('Select at least one event type');
      return;
    }

    try {
      new URL(formUrl.trim());
    } catch {
      setFormError('Invalid URL format');
      return;
    }

    setSaving(true);
    setFormError('');

    if (!profile?.organization_id || !profile?.id) return;
    const result = await adminApi.createWebhook(
      profile.organization_id,
      profile.id,
      {
        url: formUrl.trim(),
        event_types: formEvents,
        description: formDescription.trim() || undefined,
      }
    );

    if (!result) {
      setFormError('Failed to create webhook');
      setSaving(false);
      return;
    }

    setSaving(false);
    resetForm();
    fetchWebhooks();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this webhook?')) return;
    await adminApi.deleteWebhook(id);
    fetchWebhooks();
  };

  const handleToggle = async (webhook: WebhookSubscription) => {
    await adminApi.updateWebhook(webhook.id, { is_active: !webhook.is_active });
    fetchWebhooks();
  };

  const toggleSecret = (id: string) => {
    setShowSecrets((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleEvent = (event: string) => {
    setFormEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event]
    );
  };

  return (
    <div className="support-page">
      <div className="support-page-heading">
        <div>
          <h1 className="support-page-title">Webhooks</h1>
          <p className="text-muted-foreground mt-1">
            Send real-time event notifications to external services
          </p>
        </div>
        <Button variant="ghost"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          New Webhook
        </Button>
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">New Webhook</h3>
            <Button aria-label="Close" size="iconSm" variant="ghost" onClick={resetForm} className="text-muted-foreground hover:text-muted-foreground">
              <X className="w-5 h-5" />
            </Button>
          </div>

          {formError && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-sm text-error-text">
              {formError}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="webhookspage-1" className="block text-sm font-medium text-foreground mb-1">Endpoint URL</label>
              <Input id="webhookspage-1"
                type="url"
                value={formUrl}
                onChange={(e) => setFormUrl(e.target.value)}
                placeholder="https://example.com/webhook"
                className="w-full border border-border rounded-md px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-ring focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Description <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <Input
                type="text"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Slack notification integration"
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Event Types</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {availableEvents.map((event) => (
                  <label key={event} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formEvents.includes(event)}
                      onChange={() => toggleEvent(event)}
                      className="rounded border-border text-link focus:ring-ring"
                    />
                    <span className="text-sm text-foreground font-mono">{event}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <Button variant="ghost"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary disabled:bg-primary transition-colors text-sm font-medium flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Create
            </Button>
            <Button variant="ghost"
              onClick={resetForm}
              className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-border transition-colors text-sm font-medium"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Webhooks list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-link" />
        </div>
      ) : webhooks.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border border-border">
          <Webhook className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No webhooks configured</p>
          <p className="text-sm text-muted-foreground mt-1">
            Create webhooks to integrate with external services
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {webhooks.map((webhook) => (
            <div
              key={webhook.id}
              className={`bg-card rounded-lg border border-border p-5 ${
                !webhook.is_active ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <code className="text-sm text-foreground font-mono truncate">
                      {webhook.url}
                    </code>
                    {webhook.consecutive_failures > 0 ? (
                      <XCircle className="w-4 h-4 text-error-text flex-shrink-0" />
                    ) : webhook.last_success_at ? (
                      <CheckCircle2 className="w-4 h-4 text-success-foreground flex-shrink-0" />
                    ) : null}
                  </div>
                  {webhook.description && (
                    <p className="text-sm text-muted-foreground mt-1">{webhook.description}</p>
                  )}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {webhook.event_types.map((event) => (
                      <span
                        key={event}
                        className="px-1.5 py-0.5 text-[10px] font-mono bg-muted text-muted-foreground rounded"
                      >
                        {event}
                      </span>
                    ))}
                  </div>
                  {/* Secret */}
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-muted-foreground">Secret:</span>
                    <code className="text-xs text-muted-foreground font-mono">
                      {showSecrets.has(webhook.id)
                        ? webhook.secret
                        : '••••••••••••••••'}
                    </code>
                    <Button aria-label="Toggle secret visibility" size="iconSm" variant="ghost"
                      onClick={() => toggleSecret(webhook.id)}
                      className="text-muted-foreground hover:text-muted-foreground"
                    >
                      {showSecrets.has(webhook.id) ? (
                        <EyeOff className="w-3.5 h-3.5" />
                      ) : (
                        <Eye className="w-3.5 h-3.5" />
                      )}
                    </Button>
                    <Button size="iconSm" variant="ghost"
                      onClick={() => navigator.clipboard.writeText(webhook.secret)}
                      className="text-muted-foreground hover:text-muted-foreground"
                      title="Copy secret"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-4">
                  <Button variant="ghost"
                    onClick={() => handleToggle(webhook)}
                    className="flex-shrink-0"
                    title={webhook.is_active ? 'Disable' : 'Enable'}
                  >
                    {webhook.is_active ? (
                      <ToggleRight className="w-6 h-6 text-success-foreground" />
                    ) : (
                      <ToggleLeft className="w-6 h-6 text-muted-foreground" />
                    )}
                  </Button>
                  <Button aria-label="Delete item" size="iconSm" variant="ghost"
                    onClick={() => handleDelete(webhook.id)}
                    className="p-1.5 text-muted-foreground hover:text-error-text hover:bg-destructive/10 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {webhook.consecutive_failures > 0 && (
                <div className="mt-3 p-2 bg-destructive/10 border border-destructive/30 rounded text-xs text-error-text">
                  {webhook.consecutive_failures} consecutive failure{webhook.consecutive_failures > 1 ? 's' : ''}.
                  Last failure: {webhook.last_failure_at ? new Date(webhook.last_failure_at).toLocaleString() : 'Unknown'}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
